import { Intent } from "@blueprintjs/core";

/**
 * Result of the optional archive that rides along with a bulk
 * duplicate-and-re-run.
 *
 * `skipped` means the user left the archive checkbox off, which is not a
 * failure and must not warn.
 */
export type ArchiveOutcome =
    | { status: "skipped" }
    | { status: "archived"; archivedCount: number; requestedCount: number };

export const scenarioCount = (count: number): string =>
    `${count} ${count === 1 ? "scenario" : "scenarios"}`;

const originalCount = (count: number): string =>
    `${count} ${count === 1 ? "original" : "originals"}`;

/** Toast intent and message for a bulk re-run that created at least one copy. */
export const bulkRerunToast = (
    createdCount: number,
    archive: ArchiveOutcome
): { intent: Intent; message: string } => {
    const created = `${scenarioCount(
        createdCount
    )} duplicated and queued against the latest model files.`;

    if (archive.status === "skipped") {
        return { intent: Intent.SUCCESS, message: created };
    }

    if (archive.archivedCount === 0) {
        return {
            intent: Intent.WARNING,
            message: `${created} Couldn't archive the originals.`
        };
    }

    const { archivedCount, requestedCount } = archive;
    return archivedCount === requestedCount
        ? {
              intent: Intent.SUCCESS,
              message: `${created} Archived ${originalCount(archivedCount)}.`
          }
        : {
              intent: Intent.WARNING,
              message: `${created} Archived ${archivedCount} of ${requestedCount} originals.`
          };
};

/**
 * Warning for reductions the whole-project-only rule forbids.
 *
 * A scenario saved before the rule can carry a partial reduction on a project
 * the ISO only studies in service or dropped. The copy runs without it, so the
 * re-run is not the scenario the user duplicated — say which projects moved.
 */
export const droppedWholeProjectOnlyToast = (
    projectIds: ReadonlyArray<string>
): { intent: Intent; message: string } | null =>
    projectIds.length === 0
        ? null
        : {
              intent: Intent.WARNING,
              message: `${projectIds.join(
                  ", "
              )} can only be modelled in service or dropped, so the saved partial reduction was removed from the re-run.`
          };
